<template>
    <Breadcrumbs main="Design" mains="Template Report" title="Template" />
    <div class="container-fluid">
        <div class="row">
            <div class="col-sm-12">
                <div class="card">
                    <div class="card-header d-flex justify-content-between align-items-center">
                        <h5>Daftar Template</h5>
                        <!-- Button click handler remains the same, but the function logic changes -->
                        <button class="btn btn-primary" @click="openAddModal">Tambah Template Baru</button>
                    </div>
                    <div class="card-body">
                        <div class="table-responsive">
                            <table class="table table-bordered">
                                <thead>
                                    <tr>
                                        <th>Nama Template</th>
                                        <th>Deskripsi</th>
                                        <th>Preview</th>
                                        <th>Aksi</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="template in templates" :key="template.ID">
                                        <td>{{ template.name }}</td>
                                        <td>{{ template.description }}</td>
                                        <td>
                                            <!-- Preview button still uses the modal -->
                                            <button class="btn btn-sm btn-info" @click="previewTemplate(template)">Lihat Preview</button>
                                        </td>
                                        <td>
                                            <!-- Edit button click handler remains the same, but the function logic changes -->
                                            <button class="btn btn-sm btn-warning me-2" @click="openEditModal(template)">Edit</button>
                                            <button class="btn btn-sm btn-danger" @click="deleteTemplate(template.ID)">Hapus</button>
                                        </td>
                                    </tr>
                                    <tr v-if="templates.length === 0">
                                        <td colspan="4" class="text-center">Belum ada template.</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Modal Preview: Use iframe -->
        <div v-if="showPreviewModal" class="modal fade show d-block" tabindex="-1" role="dialog" style="background-color: rgba(0,0,0,0.5);">
            <div class="modal-dialog modal-xl" role="document">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Preview Template: {{ selectedTemplate?.name }}</h5>
                        <button type="button" class="btn-close" @click="closePreviewModal"></button>
                    </div>
                    <div class="modal-body col-12">
                        <iframe
                            v-if="selectedTemplate?.html_content"
                            ref="previewIframe"
                            :srcdoc="selectedTemplate.html_content"
                            style="width: 100%; height: 60vh; border: 1px solid #ccc;"
                            frameborder="0"
                        ></iframe>
                        <div v-else>
                            Konten preview tidak tersedia.
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" @click="closePreviewModal">Tutup</button>
                        <button
                            v-if="selectedTemplate?.html_content"
                            type="button"
                            class="btn btn-primary"
                            @click="printIframeContent"
                        >Print</button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Modal Tambah/Edit placeholder comment can be removed or kept as documentation -->
        <!-- Anda perlu membuat komponen modal terpisah atau menambahkan logika modal di sini -->

    </div>
</template>

<script lang="ts" setup>
// Import ref and useRouter
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router'; // Import useRouter
import * as setting from "@/api/services/SettingRequest";
import {toast} from 'vue3-toastify';

// Get router instance
const router = useRouter(); // Get the router instance

interface Template {
    id: number;
    name: string;
    description: string;
    header?: string; 
    content?: string; 
    footer?: string; 
}

const templates = ref<Template[]>([]);

// Refs for preview modal remain
const showPreviewModal = ref(false);
const selectedTemplate = ref<Template | null>(null);

// --- Fungsi CRUD (Placeholder & Navigation for Add/Edit) ---

const fetchTemplates = async () => {
    try {
        const resp = await setting.postRequest("/template/list", {page:1, size:10000,search:''});
        templates.value = resp.data;
    } catch (error) {
        console.error('Gagal memuat data template:', error);
    }
}
const openAddModal = () => {
    console.log('Navigasi ke halaman tambah template');
    router.push({ name: 'template-add' }); // Ganti 'templateDesignAdd' dengan nama route Anda untuk halaman design (mode tambah)
};

const openEditModal = (template: Template) => {
    router.push({ name: 'template-edit', params: { id: template.ID } }); // Ganti 'templateDesignEdit' dengan nama route Anda untuk halaman design (mode edit)
};

const deleteTemplate = async (id: number) => {
    console.log('Hapus template dengan ID:', id);
    if (confirm('Apakah Anda yakin ingin menghapus template ini?')) {
        await setting.postRequest("/template/delete", {ID: id});
        templates.value = templates.value.filter(t => t.ID !== id);
        toast.success('Template berhasil dihapus');
    }
};

// Preview function remains unchanged, using the modal
const previewTemplate = (template: Template) => {
    console.log('Preview template:', template.ID);

    let fullHtml = '<!DOCTYPE html>\n';
    fullHtml += '<html lang="en">\n'; // Assuming English, adjust if needed
    fullHtml += template.html_head; // Add the generated head
    fullHtml += '\n<body>\n';
    fullHtml += template.html_content; // Add the body content from the editor
    fullHtml += '\n</body>\n';
    fullHtml += '</html>';
    selectedTemplate.value = {
        name: template.name,
        html_content: fullHtml
    };
    showPreviewModal.value = true;
};


// Close preview modal function remains unchanged
const closePreviewModal = () => {
    showPreviewModal.value = false;
    selectedTemplate.value = null;
};

// --- Lifecycle Hooks atau fungsi lain jika diperlukan ---
// Misalnya, fetch data template saat komponen dimuat
onMounted(() => {
  try {
    fetchTemplates()
  } catch (error) {
    console.error('Gagal memuat data template:', error);
  }
});

const previewIframe = ref<HTMLIFrameElement | null>(null);

const printIframeContent = () => {
    const iframe = previewIframe.value;
    if (iframe && iframe.contentWindow) {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
    }
};
</script>

<style scoped>
/* Styles for modal remain unchanged */
.modal.d-block {
  display: block;
}
.modal-dialog {
  margin-top: 5%; /* Atur posisi vertikal modal */
}

/* Optional: Ensure iframe takes up modal body space */
.modal-body {
    padding: 0; /* Remove padding if iframe should touch edges */
    overflow: hidden; /* Hide potential scrollbars on the body itself */
}
.modal-body iframe {
    display: block; /* Remove potential extra space below iframe */
    border: none; /* Override default iframe border if needed */
    width: 100%; /* Ensure full width */
    height: 75vh; /* Adjust height as needed, maybe more than 60vh */
}

</style>